import React, { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Calendar, Play } from "lucide-react";
import IconContainer from "@/components/ui/icon-container";
import { ContractData } from "@/pages/contracts/types";
import { DetailedViewOptions } from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { useOnboardingStages } from "@/pages/settings/components/contractsSettings/hooks";
import { OnboardingStage } from "@/pages/settings/components/contractsSettings/types";
import { BlockedWorkflow, useMoveOnboardingStage } from "@/pages/contracts/hooks";
import OnboardingNotStartedEmptyState from "./components/OnboardingNotStartedEmptyState";
import OnboardingActiveNoStagesEmptyState from "./components/OnboardingActiveNoStagesEmptyState";
import OnboardingCompletedEmptyState from "./components/OnboardingCompletedEmptyState";
import OnboardingStagesPanel from "./OnboardingStagesPanel";
import type { ContractFieldUpdate } from "../ContractDetailedView";

export interface ContractOnboardingState {
  parsedStartDate: Date | null;
  isCompleted: boolean;
  isActive: boolean;
  isScheduled: boolean;
  hasStages: boolean;
  stages: OnboardingStage[];
  isActiveWithNoStages: boolean;
  activeStage: string | null;
  activeStageColor: string | null;
  currentStageId: string | null;
  isDatePickerOpen: boolean;
  setIsDatePickerOpen: (open: boolean) => void;
  handleStart: () => void;
  handleSelectStartDate: (date: Date) => void;
  handleOpenDatePickerFromHeader: () => void;
  handleSelectStage: (stageId: string) => void;
  handleComplete: () => void;
  handleBackToNotStarted: () => void;
  handleBackToOnboarding: () => void;
  guardrailsBlockedBy: BlockedWorkflow[];
  setGuardrailsBlockedBy: (blockedBy: BlockedWorkflow[]) => void;
  workflowToOpen: string | null;
  clearWorkflowToOpen: () => void;
  handleOpenBlockedWorkflow: (workflowId: string) => void;
}

export function useContractOnboarding(
  contract: ContractData | undefined,
  onUpdateField: ContractFieldUpdate,
  onSwitchToOnboardingTab: () => void
): ContractOnboardingState {
  const { t } = useTranslation();
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [guardrailsBlockedBy, setGuardrailsBlockedBy] = useState<BlockedWorkflow[]>([]);
  const [workflowToOpen, setWorkflowToOpen] = useState<string | null>(null);
  const moveStage = useMoveOnboardingStage();
  const moveStageRef = useRef(moveStage);
  moveStageRef.current = moveStage;

  const handleStart = useCallback(() => {
    onUpdateField("onboardingStartDate", new Date().toISOString());
  }, [onUpdateField]);

  const handleSelectStartDate = useCallback(
    (date: Date) => {
      onUpdateField("onboardingStartDate", date.toISOString());
      setIsDatePickerOpen(false);
    },
    [onUpdateField]
  );

  const handleOpenDatePickerFromHeader = useCallback(() => {
    onSwitchToOnboardingTab();
    requestAnimationFrame(() => setIsDatePickerOpen(true));
  }, [onSwitchToOnboardingTab]);

  const handleSelectStage = useCallback(
    (stageId: string) => {
      if (!contract?.id) return;
      const previousStageId = contract.onboardingStageId ?? null;
      moveStageRef.current.mutate(
        { pathParams: { contractId: contract.id, stageId }, data: {} },
        {
          onSuccess: (result) => {
            if (result.onboardingStageId) {
              onUpdateField("onboardingStageId", result.onboardingStageId);
            }
          },
          onError: (error: unknown) => {
            onUpdateField("onboardingStageId", previousStageId);
            const axiosError = error as { response?: { status?: number; data?: { blockedBy?: BlockedWorkflow[] } } };
            if (axiosError.response?.status === 400) {
              const blocked = axiosError.response.data?.blockedBy ?? [];
              setGuardrailsBlockedBy(blocked);
            } else {
              toast.error(t("contracts.detailedView.updateError"));
            }
          }
        }
      );
    },
    [contract?.id, contract?.onboardingStageId, onUpdateField, t]
  );

  const handleOpenBlockedWorkflow = useCallback(
    (workflowId: string) => {
      setGuardrailsBlockedBy([]);
      setWorkflowToOpen(workflowId);
      onSwitchToOnboardingTab();
    },
    [onSwitchToOnboardingTab]
  );

  const clearWorkflowToOpen = useCallback(() => setWorkflowToOpen(null), []);

  const handleComplete = useCallback(() => {
    onUpdateField("onboardingCompletedAt", new Date().toISOString());
  }, [onUpdateField]);

  const handleBackToNotStarted = useCallback(() => {
    onUpdateField("onboardingStartDate", null);
  }, [onUpdateField]);

  const handleBackToOnboarding = useCallback(() => {
    onUpdateField("onboardingCompletedAt", null);
  }, [onUpdateField]);

  const parsedStartDate = contract?.onboardingStartDate
    ? new Date(contract.onboardingStartDate)
    : null;
  const isCompleted = contract?.onboardingCompletedAt != null;
  const isActive =
    !isCompleted && parsedStartDate !== null && parsedStartDate <= new Date();
  const isScheduled =
    !isCompleted && parsedStartDate !== null && parsedStartDate > new Date();
  const { data: stagesResponse } = useOnboardingStages();
  const stages: OnboardingStage[] = stagesResponse?.stages ?? [];
  const hasStages = stages.length > 0;
  const isActiveWithNoStages = isActive && !hasStages;
  const orderedStages = [...stages].sort(
    (a, b) => (a.itemOrder ?? 0) - (b.itemOrder ?? 0)
  );
  const currentStage =
    orderedStages.find((stage) => stage.id === contract?.onboardingStageId) ??
    orderedStages[0] ??
    null;
  const activeStage = currentStage?.name ?? null;
  const activeStageColor = currentStage?.stageColor ?? null;
  const currentStageId = currentStage?.id ?? null;

  return {
    parsedStartDate,
    isCompleted,
    isActive,
    isScheduled,
    hasStages,
    stages: orderedStages,
    isActiveWithNoStages,
    activeStage,
    activeStageColor,
    currentStageId,
    isDatePickerOpen,
    setIsDatePickerOpen,
    handleStart,
    handleSelectStartDate,
    handleOpenDatePickerFromHeader,
    handleSelectStage,
    handleComplete,
    handleBackToNotStarted,
    handleBackToOnboarding,
    guardrailsBlockedBy,
    setGuardrailsBlockedBy,
    workflowToOpen,
    clearWorkflowToOpen,
    handleOpenBlockedWorkflow
  };
}

export function buildOnboardingTopOptions(
  state: ContractOnboardingState,
  onboardingEnabled: boolean
): DetailedViewOptions | undefined {
  if (!onboardingEnabled) return undefined;
  if (state.isCompleted) {
    return {
      backToOnboarding: { action: state.handleBackToOnboarding }
    };
  }
  if (state.isScheduled) {
    return {
      startOnboardingNow: {
        action: state.handleStart,
        customIcon: (
          <IconContainer size="5" color="secondary">
            <Play />
          </IconContainer>
        )
      },
      changeOnboardingStartDate: {
        action: state.handleOpenDatePickerFromHeader,
        customIcon: (
          <IconContainer size="5" color="secondary">
            <Calendar />
          </IconContainer>
        )
      }
    };
  }
  if (!state.isActive) {
    return {
      chooseOnboardingStartDate: {
        action: state.handleOpenDatePickerFromHeader,
        customIcon: (
          <IconContainer size="5" color="secondary">
            <Calendar />
          </IconContainer>
        )
      }
    };
  }
  return {
    backToOnboardingNotStarted: { action: state.handleBackToNotStarted }
  };
}

interface Props {
  contract: ContractData;
  state: ContractOnboardingState;
}

const OnboardingTab: React.FC<Props> = ({ contract, state }) => {
  if (state.isCompleted) {
    if (!state.hasStages) {
      return <OnboardingCompletedEmptyState />;
    }
    return (
      <OnboardingStagesPanel
        contractId={contract.id}
        stages={state.stages}
        currentStageId={contract.onboardingStageId}
        onProceedStage={state.handleSelectStage}
        onComplete={state.handleComplete}
        isCompleted
      />
    );
  }
  if (!state.isActive) {
    return (
      <OnboardingNotStartedEmptyState
        onboardingStartDate={contract.onboardingStartDate}
        isDatePickerOpen={state.isDatePickerOpen}
        setIsDatePickerOpen={state.setIsDatePickerOpen}
        onSelectStartDate={state.handleSelectStartDate}
        onStartNow={state.handleStart}
      />
    );
  }
  if (!state.hasStages) {
    return (
      <OnboardingActiveNoStagesEmptyState
        onCompleteOnboarding={state.handleComplete}
      />
    );
  }
  return (
    <OnboardingStagesPanel
      contractId={contract.id}
      stages={state.stages}
      currentStageId={contract.onboardingStageId}
      onProceedStage={state.handleSelectStage}
      onComplete={state.handleComplete}
      workflowToOpen={state.workflowToOpen}
      onWorkflowOpened={state.clearWorkflowToOpen}
    />
  );
};

export default OnboardingTab;
